/**
 * Time Series Builder
 *
 * Builds time series data for line graphs based on the calculator's
 * time series strategy. Supports multiple strategies for different
 * client types.
 *
 * Output format: { data: [[timestamp_ms, value, 0, 0], ...], totalValue: number }
 */

import { TimeSeriesStrategies } from '../calculators/GraphTypes.js';
import { isPastOrDateless } from '../helpers/event-utils.js';
import { getEffectiveDenomination } from '../helpers/denomination-resolver.js';

const TimeSeriesBuilder = {
	/**
	 * Build time series data based on strategy
	 * @param {string} strategy - TimeSeriesStrategies constant
	 * @param {Object} input - { clientData, userData, client, shareClasses }
	 * @returns {Object} { data: Array, totalValue: number }
	 */
	build: function (strategy, input) {
		console.log('[TimeSeriesBuilder] Building time series with strategy:', strategy);

		switch (strategy) {
			case TimeSeriesStrategies.FUND_CAPITAL_ACCOUNT:
				return this.buildFundTimeSeries(input);
			case TimeSeriesStrategies.VALUATION_BASED:
				return this.buildValuationTimeSeries(input);
			case TimeSeriesStrategies.VALUATION_BY_CLASS:
				return this.buildValuationByClassTimeSeries(input);
			case TimeSeriesStrategies.SINGLE_CLASS:
				return this.buildSingleClassTimeSeries(input);
			case TimeSeriesStrategies.INCOME_ALLOCATIONS:
				return this.buildIncomeAllocationsTimeSeries(input);
			default:
				console.warn('[TimeSeriesBuilder] Unknown strategy:', strategy);
				return { data: [], totalValue: 0 };
		}
	},

	/**
	 * Fund Capital Account strategy
	 * Replays contribution/distribution/RoC events to build cumulative value
	 * Total Value at each point = Capital Account + Distributions
	 */
	buildFundTimeSeries: function (input) {
		const { clientData, userData, client, shareClasses, currentTotalValue } = input;
		const events = [];

		// Gather all events across share classes
		shareClasses.forEach(function (shareClass) {
			const userShares = userData.clients?.[client]?.shares?.[shareClass];
			if (!userShares?.events) return;

			const nowTs = Math.floor(Date.now() / 1000);
			const clientEvents = (clientData.equity?.[shareClass]?.events || []).filter((e) => isPastOrDateless(e, nowTs));

			userShares.events.forEach(function (userEvent) {
				const clientEvent = clientEvents.find((e) => e.nrid === userEvent.nrid);
				if (!clientEvent) return;

				const date = Number(clientEvent.date) * 1000; // Convert to ms
				const amount = Number(userEvent.grossDollars) || 0;

				// Map event types to their effect on capital account
				switch (clientEvent.eventType) {
					case 'Contribution':
						events.push({ date, amount, type: 'contribution' });
						break;
					case 'Distribution':
					case 'Cash Dividend':
						events.push({ date, amount, type: 'distribution' });
						break;
					case 'Return of Capital':
					case 'Redemption':
						events.push({ date, amount: -amount, type: 'roc' });
						break;
				}
			});
		});

		// Sort by date
		events.sort((a, b) => a.date - b.date);

		// Build cumulative series
		let capitalAccount = 0;
		let totalDistributions = 0;
		const timeSeries = [];

		events.forEach(function (event) {
			if (event.type === 'contribution') {
				capitalAccount += event.amount;
			} else if (event.type === 'distribution') {
				totalDistributions += event.amount;
			} else if (event.type === 'roc') {
				capitalAccount += event.amount; // Negative amount reduces capital
			}

			// Total Value = Capital Account + Distributions
			const totalValue = capitalAccount + totalDistributions;
			timeSeries.push([event.date, totalValue, 0, 0]);
		});

		// Add current point using the calculator's total value if provided
		// This ensures the "now" point matches the headline value
		const nowValue = currentTotalValue || (timeSeries.length > 0 ? timeSeries[timeSeries.length - 1][1] : 0);
		if (timeSeries.length > 0) {
			timeSeries.push([Date.now(), nowValue, 0, 0]);
		}

		console.log('[TimeSeriesBuilder] Fund time series built:', timeSeries.length, 'points');

		return {
			data: timeSeries,
			totalValue: nowValue,
		};
	},

	/**
	 * Valuation-based strategy
	 * Uses share price valuations to build value over time
	 * Value = shares owned × (sharePrice - strikePrice) for each valuation date
	 */
	buildValuationTimeSeries: function (input) {
		const { clientData, userData, client, shareClasses, currentTotalValue } = input;
		const dataPoints = {};

		shareClasses.forEach(function (shareClass) {
			const equity = clientData.equity?.[shareClass];
			if (!equity?.valuations?.length) return;

			const userOwned = Number(userData.clients?.[client]?.shares?.[shareClass]?.owned) || 0;
			if (userOwned <= 0) return;

			// Find user's first acquisition date for this class
			// Only show valuations from when they actually owned the shares
			let firstAcquisitionDate = null;
			let strikePrice = 0;

			if (equity.transactions) {
				const userTxns = equity.transactions.filter((t) => t.sender === userData.userid || t.receiver === userData.userid);

				// Find earliest transaction where user received shares
				const acquisitions = userTxns
					.filter((t) => {
						const qty = Number(t.quantity) || 0;
						return (t.receiver === userData.userid && qty > 0) || (t.sender === userData.userid && qty < 0);
					})
					.map((t) => Number(t.date))
					.filter((d) => !isNaN(d) && d > 0);
				if (acquisitions.length > 0) {
					firstAcquisitionDate = Math.min(...acquisitions) * 1000; // Convert to ms
					console.log(`[TimeSeriesBuilder] ${shareClass}: First acquisition on ${new Date(firstAcquisitionDate).toDateString()}`);
				}

				// Get strike price for options (if applicable)
				userTxns.forEach(function (t) {
					if (t.strikePrice) strikePrice = Number(t.strikePrice);
				});
			}

			// Build time series from valuations
			// If user has a firstAcquisitionDate, use that as the starting point
			// with the valuation that was in effect at that time (prior valuation)
			if (firstAcquisitionDate !== null) {
				// Find the most recent valuation at or before the acquisition date
				const priorValuations = equity.valuations
					.filter((v) => Number(v.date) * 1000 <= firstAcquisitionDate)
					.sort((a, b) => Number(b.date) - Number(a.date)); // Most recent first

				if (priorValuations.length > 0) {
					const priorValuation = priorValuations[0];
					const price = Number(priorValuation.sharePrice) || 0;
					const inTheMoneyValue = Math.max(0, userOwned * (price - strikePrice));

					// Add data point at the acquisition date using prior valuation
					if (dataPoints[firstAcquisitionDate]) {
						dataPoints[firstAcquisitionDate] += inTheMoneyValue;
					} else {
						dataPoints[firstAcquisitionDate] = inTheMoneyValue;
					}
					console.log(
						`[TimeSeriesBuilder] ${shareClass}: Added acquisition point at ${new Date(firstAcquisitionDate).toDateString()} with price $${price}`
					);
				}
			}

			equity.valuations.forEach(function (valuation) {
				const date = Number(valuation.date) * 1000; // Convert to ms

				// Skip valuations from before the user acquired this class
				if (firstAcquisitionDate !== null && date < firstAcquisitionDate) {
					return; // Don't show values from before user owned shares
				}

				// Skip if this is the acquisition date (already added above)
				if (firstAcquisitionDate !== null && date === firstAcquisitionDate) {
					return;
				}

				const price = Number(valuation.sharePrice) || 0;
				// In-the-money value only - floor at zero when underwater
				const inTheMoneyValue = Math.max(0, userOwned * (price - strikePrice));

				// Aggregate values at same date
				if (dataPoints[date]) {
					dataPoints[date] += inTheMoneyValue;
				} else {
					dataPoints[date] = inTheMoneyValue;
				}
			});
		});

		// Sort and format for graphArray
		const dates = Object.keys(dataPoints)
			.map(Number)
			.sort((a, b) => a - b);
		const timeSeries = dates.map(function (date) {
			return [date, dataPoints[date], 0, 0];
		});

		// Add current point using the calculator's total value if provided
		// This ensures the "now" point matches the headline value
		const nowValue = currentTotalValue || (timeSeries.length > 0 ? timeSeries[timeSeries.length - 1][1] : 0);
		if (timeSeries.length > 0) {
			timeSeries.push([Date.now(), nowValue, 0, 0]);
		}

		console.log('[TimeSeriesBuilder] Valuation time series built:', timeSeries.length, 'points');

		return {
			data: timeSeries,
			totalValue: nowValue,
		};
	},

	/**
	 * Valuation by class strategy
	 * Builds per-class time series for stacked area chart
	 * Output format: { data: [[timestamp, class1Val, class2Val, ...], ...], shareClasses: [...], totalValue: number }
	 *
	 * @param {Object} input - Configuration object
	 * @param {Object} input.vestedSharesByClass - Map of classKey -> vested share count (for vested-only graphs)
	 * @param {boolean} input.useVestedOnly - If true, use vested shares instead of owned
	 */
	buildValuationByClassTimeSeries: function (input) {
		const { clientData, userData, client, shareClasses, currentTotalValue, vestedSharesByClass, useVestedOnly } = input;

		// Track per-class data: { shareClass: { strikePrice, owned, valuations: [{date, value}], firstAcquisition } }
		const classData = {};
		const allDates = new Set();

		// First pass: gather all data per class
		shareClasses.forEach(function (shareClass) {
			const equity = clientData.equity?.[shareClass];

			// Check if this class is an option type (uses strike price semantics)
			// RSUs and regular shares don't have strike prices
			// Must match calculator logic in index.js which uses useStrike boolean
			const hasStrikePriceType = equity?.useStrike === true;

			const userOwned = Number(userData.clients?.[client]?.shares?.[shareClass]?.owned) || 0;
			if (userOwned <= 0) return;

			// When useVestedOnly is enabled, use vested shares for graph calculation
			// This shows only the value of fully vested shares
			const graphShares = useVestedOnly && vestedSharesByClass?.[shareClass] !== undefined ? vestedSharesByClass[shareClass] : userOwned;

			// Skip classes with no vested shares in vested-only mode (falls back to image)
			if (useVestedOnly && graphShares <= 0) {
				return;
			}

			// Find user's first acquisition date for this class
			// Only show valuations from when they actually owned the shares
			let firstAcquisitionDate = null;
			if (equity?.transactions) {
				const userTxns = equity.transactions.filter((t) => t.sender === userData.userid || t.receiver === userData.userid);
				// Find earliest transaction where user received shares
				const acquisitions = userTxns
					.filter((t) => {
						const qty = Number(t.quantity) || 0;
						// User received shares if they're the receiver with positive qty
						// or sender with negative qty (rare but possible in some systems)
						return (t.receiver === userData.userid && qty > 0) || (t.sender === userData.userid && qty < 0);
					})
					.map((t) => Number(t.date))
					.filter((d) => !isNaN(d) && d > 0);
				if (acquisitions.length > 0) {
					firstAcquisitionDate = Math.min(...acquisitions) * 1000; // Convert to ms
					console.log(`[TimeSeriesBuilder] ${shareClass}: First acquisition on ${new Date(firstAcquisitionDate).toDateString()}`);
				}
			}

			// For vested-only mode, find the first date when shares actually vested
			// This overrides firstAcquisitionDate so graph starts from first vesting event
			let firstVestedDate = null;
			if (useVestedOnly) {
				const userShares = userData.clients?.[client]?.shares?.[shareClass];
				if (userShares?.vesting) {
					const vestingDates = [];
					// Collect all vesting schedule dates that are in the past
					Object.values(userShares.vesting).forEach(function (issue) {
						if (issue?.schedule) {
							Object.keys(issue.schedule).forEach(function (timestamp) {
								const dateMs = Number(timestamp) * 1000;
								if (dateMs <= Date.now()) {
									vestingDates.push(dateMs);
								}
							});
						}
					});
					if (vestingDates.length > 0) {
						firstVestedDate = Math.min(...vestingDates);
					}
				}
				// Use first vested date instead of acquisition date for vested-only graphs
				if (firstVestedDate !== null) {
					firstAcquisitionDate = firstVestedDate;
				}
			}

			// Get strike price only for option-type instruments
			let strikePrice = 0;
			if (hasStrikePriceType && equity?.transactions) {
				const userTxns = equity.transactions.filter((t) => t.sender === userData.userid || t.receiver === userData.userid);
				userTxns.forEach(function (t) {
					if (t.strikePrice) strikePrice = Number(t.strikePrice);
				});
			}

			// For options without their own valuations, find underlying stock class
			let valuationsSource = equity;
			if (hasStrikePriceType && !equity?.valuations?.length) {
				// Find first non-option class with valuations
				const stockClasses = Object.keys(clientData.equity).filter((cls) => {
					const eq = clientData.equity[cls];
					return eq?.valuations?.length > 0 && !eq?.useStrike;
				});
				if (stockClasses.length > 0) {
					valuationsSource = clientData.equity[stockClasses[0]];
				}
			}

			if (!valuationsSource?.valuations?.length) return;

			// Store class info (use graphShares for value calculations)
			classData[shareClass] = {
				strikePrice: strikePrice,
				owned: graphShares, // Use vested shares when useVestedOnly is enabled
				valuations: {},
				firstAcquisitionDate: firstAcquisitionDate,
			};

			// Build valuations map for this class
			// If user has a firstAcquisitionDate, use that as the starting point
			// with the valuation that was in effect at that time (prior valuation)
			let addedAcquisitionPoint = false;

			if (firstAcquisitionDate !== null) {
				// Find the most recent valuation at or before the acquisition date
				const priorValuations = valuationsSource.valuations
					.filter((v) => Number(v.date) * 1000 <= firstAcquisitionDate)
					.sort((a, b) => Number(b.date) - Number(a.date)); // Most recent first

				if (priorValuations.length > 0) {
					const priorValuation = priorValuations[0];
					const price = Number(priorValuation.sharePrice) || 0;
					const value = Math.max(0, graphShares * (price - strikePrice));

					// Add data point at the acquisition date using prior valuation
					classData[shareClass].valuations[firstAcquisitionDate] = value;
					allDates.add(firstAcquisitionDate);
					addedAcquisitionPoint = true;
					console.log(
						`[TimeSeriesBuilder] ${shareClass}: Added acquisition point at ${new Date(firstAcquisitionDate).toDateString()} with price $${price}`
					);
				}
			}

			valuationsSource.valuations.forEach(function (valuation) {
				const date = Number(valuation.date) * 1000;

				// Skip valuations from before the user acquired this class
				if (firstAcquisitionDate !== null && date < firstAcquisitionDate) {
					return; // Don't show values from before user owned shares
				}

				// Skip if this is the same date as the acquisition point we already added
				if (addedAcquisitionPoint && date === firstAcquisitionDate) {
					return;
				}

				const price = Number(valuation.sharePrice) || 0;
				const value = Math.max(0, graphShares * (price - strikePrice));

				classData[shareClass].valuations[date] = value;
				allDates.add(date);
			});
		});

		// Get ordered list of classes that have data
		const activeClasses = shareClasses.filter((c) => classData[c]);
		if (activeClasses.length === 0) {
			return { data: [], shareClasses: [], totalValue: 0 };
		}

		// Sort dates
		const sortedDates = Array.from(allDates).sort((a, b) => a - b);

		// Build time series with per-class values
		// For each date, carry forward the last known value for classes without a valuation on that date
		const lastKnownValues = {};
		activeClasses.forEach((c) => (lastKnownValues[c] = 0));

		const timeSeries = sortedDates.map(function (date) {
			const row = [date];

			activeClasses.forEach(function (shareClass) {
				// Use this date's value if available, otherwise carry forward
				if (classData[shareClass].valuations[date] !== undefined) {
					lastKnownValues[shareClass] = classData[shareClass].valuations[date];
				}
				row.push(lastKnownValues[shareClass]);
			});

			return row;
		});

		// Calculate current total and add "now" point
		let currentTotal = 0;
		activeClasses.forEach((c) => (currentTotal += lastKnownValues[c]));

		const nowValue = currentTotalValue || currentTotal;
		if (timeSeries.length > 0) {
			// Add current point - distribute proportionally if we have a different total
			const nowRow = [Date.now()];
			if (currentTotalValue && currentTotal > 0) {
				const scaleFactor = currentTotalValue / currentTotal;
				activeClasses.forEach((c) => nowRow.push(lastKnownValues[c] * scaleFactor));
			} else {
				activeClasses.forEach((c) => nowRow.push(lastKnownValues[c]));
			}
			timeSeries.push(nowRow);
		}

		console.log('[TimeSeriesBuilder] Valuation by class time series built:', timeSeries.length, 'points,', activeClasses.length, 'classes');

		return {
			data: timeSeries,
			shareClasses: activeClasses,
			totalValue: nowValue,
		};
	},

	/**
	 * Single class time series strategy
	 * Builds time series for a specific share class (for class-specific view)
	 * Output format: { data: [[timestamp_ms, value, 0, 0], ...], totalValue: number }
	 *
	 * @param {Object} input - { clientData, userData, client, shareClass, currentValue, vestedShares, strikePrice }
	 */
	buildSingleClassTimeSeries: function (input) {
		const { clientData, userData, client, shareClass, currentValue, vestedShares, strikePrice: calcStrikePrice, useVestedOnly } = input;
		const equity = clientData.equity?.[shareClass];

		if (!equity?.valuations?.length) {
			console.log('[TimeSeriesBuilder] No valuations for single class:', shareClass);
			return { data: [], totalValue: 0 };
		}

		const userShares = userData.clients?.[client]?.shares?.[shareClass];
		const userOwned = Number(userShares?.owned) || 0;
		if (userOwned <= 0) {
			console.log('[TimeSeriesBuilder] User owns no shares in class:', shareClass);
			return { data: [], totalValue: 0 };
		}

		// Use calculator's vested shares if provided, otherwise fall back to local calculation
		// This ensures graph values match the headline display
		let graphShares;
		if (vestedShares !== undefined) {
			graphShares = vestedShares;
		} else {
			// Fallback: For RSU-style vesting, "approved" represents the vested quantity
			graphShares = userShares?.vesting ? Number(userShares?.approved || userOwned) : userOwned;
		}

		// Skip if no vested shares in vested-only mode (falls back to image)
		if (useVestedOnly && graphShares <= 0) {
			return { data: [], totalValue: 0 };
		}

		// Check if option-type (uses strike price)
		// Must match calculator logic in index.js which uses useStrike boolean
		const hasStrikePriceType = equity?.useStrike === true;

		// Use calculator's strike price if provided, otherwise calculate from transactions
		let strikePrice = calcStrikePrice || 0;
		if (!strikePrice && hasStrikePriceType && equity?.transactions) {
			const userTxns = equity.transactions.filter((t) => t.sender === userData.userid || t.receiver === userData.userid);
			userTxns.forEach(function (t) {
				if (t.strikePrice) strikePrice = Number(t.strikePrice);
			});
		}

		// Find first acquisition date
		let firstAcquisitionDate = null;
		if (equity?.transactions) {
			const userTxns = equity.transactions.filter((t) => t.sender === userData.userid || t.receiver === userData.userid);
			const acquisitions = userTxns
				.filter((t) => {
					const qty = Number(t.quantity) || 0;
					return (t.receiver === userData.userid && qty > 0) || (t.sender === userData.userid && qty < 0);
				})
				.map((t) => Number(t.date))
				.filter((d) => !isNaN(d) && d > 0);
			if (acquisitions.length > 0) {
				firstAcquisitionDate = Math.min(...acquisitions) * 1000;
				console.log(`[TimeSeriesBuilder] ${shareClass}: First acquisition on ${new Date(firstAcquisitionDate).toDateString()}`);
			}
		}

		// For vested-only mode, find the first date when shares actually vested
		// This overrides firstAcquisitionDate so graph starts from first vesting event
		if (useVestedOnly && userShares?.vesting) {
			const vestingDates = [];
			// Collect all vesting schedule dates that are in the past
			Object.values(userShares.vesting).forEach(function (issue) {
				if (issue?.schedule) {
					Object.keys(issue.schedule).forEach(function (timestamp) {
						const dateMs = Number(timestamp) * 1000;
						if (dateMs <= Date.now()) {
							vestingDates.push(dateMs);
						}
					});
				}
			});
			if (vestingDates.length > 0) {
				const firstVestedDate = Math.min(...vestingDates);
				// Use first vested date instead of acquisition date
				firstAcquisitionDate = firstVestedDate;
			}
		}

		// For options without their own valuations, find underlying stock class
		let valuationsSource = equity;
		if (hasStrikePriceType && !equity?.valuations?.length) {
			const stockClasses = Object.keys(clientData.equity).filter((cls) => {
				const eq = clientData.equity[cls];
				return eq?.valuations?.length > 0 && !eq?.useStrike;
			});
			if (stockClasses.length > 0) {
				valuationsSource = clientData.equity[stockClasses[0]];
			}
		}

		const timeSeries = [];

		// Add acquisition point if we have a prior valuation
		if (firstAcquisitionDate !== null) {
			const priorValuations = valuationsSource.valuations
				.filter((v) => Number(v.date) * 1000 <= firstAcquisitionDate)
				.sort((a, b) => Number(b.date) - Number(a.date));

			if (priorValuations.length > 0) {
				const price = Number(priorValuations[0].sharePrice) || 0;
				const value = Math.max(0, graphShares * (price - strikePrice));
				timeSeries.push([firstAcquisitionDate, value, 0, 0]);
			}
		}

		// Build time series from valuations, collecting all data points
		const dataPoints = {}; // date -> value

		valuationsSource.valuations.forEach(function (valuation) {
			const date = Number(valuation.date) * 1000;

			// Debug logging for future dates
			if (date > Date.now()) {
				console.log(`[Debug] Future valuation found for ${shareClass}:`, {
					date: new Date(date),
					timestamp: date,
					valuation: valuation,
				});
			}

			// Skip valuations from before user acquired this class
			if (firstAcquisitionDate !== null && date < firstAcquisitionDate) {
				return;
			}

			// Skip if this is the same as acquisition date (already added)
			if (firstAcquisitionDate !== null && date === firstAcquisitionDate) {
				return;
			}

			// Skip future valuations (beyond today)
			if (date > Date.now()) {
				return;
			}

			const price = Number(valuation.sharePrice) || 0;
			const value = Math.max(0, graphShares * (price - strikePrice));
			dataPoints[date] = value;
		});

		// Add acquisition point if we have one
		if (firstAcquisitionDate !== null && timeSeries.length > 0) {
			dataPoints[firstAcquisitionDate] = timeSeries[0][1];
		}

		// Get all dates and sort them
		const allDates = Object.keys(dataPoints)
			.map(Number)
			.sort((a, b) => a - b);

		// Forward-fill: ensure we have data points for each year
		// This creates a consistent x-axis with the portfolio graph
		const filledTimeSeries = [];
		let lastValue = 0;

		if (allDates.length > 0) {
			const startYear = new Date(allDates[0]).getFullYear();
			const currentDate = new Date();
			const currentYear = currentDate.getFullYear();
			// Only include current year if we have actual data for it
			// Otherwise stop at previous year to avoid creating future synthetic points
			const latestDataYear = allDates.length > 0 ? new Date(Math.max(...allDates)).getFullYear() : currentYear - 1;
			const endYear = Math.min(currentYear, latestDataYear);

			for (let year = startYear; year <= endYear; year++) {
				// Check if we have any data points in this year
				const pointsInYear = allDates.filter((d) => {
					const pointYear = new Date(d).getFullYear();
					return pointYear === year;
				});

				if (pointsInYear.length > 0) {
					// Use the latest point in this year
					const latestPoint = pointsInYear[pointsInYear.length - 1];
					lastValue = dataPoints[latestPoint];
					filledTimeSeries.push([latestPoint, lastValue, 0, 0]);
				} else if (lastValue > 0) {
					// No data point this year - forward-fill with last known value
					// Use mid-year as the synthetic point
					const midYear = new Date(year, 6, 1).getTime();
					filledTimeSeries.push([midYear, lastValue, 0, 0]);
				}
			}
		}

		// Add current point
		const nowValue = currentValue || (filledTimeSeries.length > 0 ? filledTimeSeries[filledTimeSeries.length - 1][1] : 0);
		if (filledTimeSeries.length > 0) {
			filledTimeSeries.push([Date.now(), nowValue, 0, 0]);
		}

		// For vested-only mode, ensure we always have at least 2 points (first vested date + today)
		// This prevents fallback to price graph when there are no valuations after first vesting
		if (useVestedOnly && filledTimeSeries.length < 2 && firstAcquisitionDate !== null) {
			// Find the prior valuation (at or before first vested date) - consistent with acquisition point logic
			const priorValuations = valuationsSource.valuations
				.filter((v) => Number(v.date) * 1000 <= firstAcquisitionDate)
				.sort((a, b) => Number(b.date) - Number(a.date));

			if (priorValuations.length > 0) {
				const priorPrice = Number(priorValuations[0].sharePrice) || 0;
				const firstVestedValue = Math.max(0, graphShares * (priorPrice - strikePrice));
				const todayValue = currentValue || firstVestedValue;

				// Create 2-point time series: first vested date and today
				filledTimeSeries.length = 0; // Clear any partial data
				filledTimeSeries.push([firstAcquisitionDate, firstVestedValue, 0, 0]);
				filledTimeSeries.push([Date.now(), todayValue, 0, 0]);
			}
		}

		console.log('[TimeSeriesBuilder] Single class time series built:', filledTimeSeries.length, 'points for', shareClass);

		// Debug: Log all dates in the time series
		const dates = filledTimeSeries.map((point) => new Date(point[0]));
		console.log(`[Debug] ${shareClass} time series dates:`, dates);
		if (dates.length > 0) {
			console.log(`[Debug] ${shareClass} first date:`, dates[0]);
			console.log(`[Debug] ${shareClass} last date:`, dates[dates.length - 1]);
			console.log(`[Debug] ${shareClass} now:`, new Date(Date.now()));
		}

		return {
			data: filledTimeSeries,
			totalValue: nowValue,
		};
	},

	/**
	 * Income Allocations time series strategy
	 * Builds dual-line graph: Tax Basis (from events) + Market Value (from valuations)
	 * Tax Basis at each point = Contributions + Income Allocations - Distributions
	 * Market Value at each point = shares owned × share price
	 */
	buildIncomeAllocationsTimeSeries: function (input) {
		const { clientData, userData, client, shareClasses, currentTotalValue } = input;
		const events = [];
		const valuationPoints = [];

		// Gather total shares owned across all share classes
		let totalOwned = 0;
		shareClasses.forEach(function (shareClass) {
			const userShares = userData.clients?.[client]?.shares?.[shareClass];
			totalOwned += Number(userShares?.owned) || 0;
		});

		// Gather all events across share classes
		shareClasses.forEach(function (shareClass) {
			const userShares = userData.clients?.[client]?.shares?.[shareClass];
			if (!userShares?.events) return;

			const nowTs = Math.floor(Date.now() / 1000);
			const clientEvents = (clientData.equity?.[shareClass]?.events || []).filter((e) => isPastOrDateless(e, nowTs));

			userShares.events.forEach(function (userEvent) {
				const clientEvent = clientEvents.find((e) => e.nrid === userEvent.nrid);
				if (!clientEvent) return;

				const date = Number(clientEvent.date) * 1000; // Convert to ms
				const amount = Number(userEvent.grossDollars) || 0;

				// Map event types to their effect on tax basis
				switch (clientEvent.eventType) {
					case 'Contribution':
						events.push({ date, amount, type: 'contribution' });
						break;
					case 'Gain (Loss) Accrual':
						events.push({ date, amount, type: 'income_allocation' });
						break;
					case 'Distribution':
					case 'Cash Dividend':
						events.push({ date, amount, type: 'distribution' });
						break;
				}
			});

			// Gather transfer transactions for this user
			const transactions = clientData.equity?.[shareClass]?.transactions || [];
			const userId = userData.userid?.toLowerCase();
			transactions.forEach(function (t) {
				if (!t.date) return;
				const date = Number(t.date) * 1000;
				// quantity × sharePrice for equity transfers; fall back to quantity alone
				// for fund capital transfers where quantity IS the dollar amount
				const qty = Number(t.quantity) || 0;
				const price = Number(t.sharePrice) || 0;
				const amount = price > 0 ? qty * price : qty;
				if (!amount) return;
				const senderLower = t.sender?.toLowerCase();
				const receiverLower = t.receiver?.toLowerCase();
				if (receiverLower === userId) {
					events.push({ date, amount, type: 'transfer_in' });
				} else if (senderLower === userId) {
					events.push({ date, amount, type: 'transfer_out' });
				}
			});

			// Gather valuations for market value line
			const equity = clientData.equity?.[shareClass];
			if (equity?.valuations?.length) {
				equity.valuations.forEach(function (valuation) {
					const date = Number(valuation.date) * 1000;
					const sharePrice = Number(valuation.sharePrice) || 0;
					valuationPoints.push({ date, sharePrice });
				});
			}
		});

		// Sort events and valuations by date
		events.sort((a, b) => a.date - b.date);
		valuationPoints.sort((a, b) => a.date - b.date);

		// Build tax basis map by date
		// Initial basis comes from Contribution events
		let totalContributions = 0;
		let totalIncomeAllocations = 0;
		let totalDistributions = 0;
		const taxBasisByDate = {};

		let totalTransfers = 0;

		events.forEach(function (event) {
			if (event.type === 'contribution') {
				totalContributions += event.amount;
			} else if (event.type === 'income_allocation') {
				totalIncomeAllocations += event.amount;
			} else if (event.type === 'distribution') {
				totalDistributions += event.amount;
			} else if (event.type === 'transfer_in') {
				totalTransfers += event.amount;
			} else if (event.type === 'transfer_out') {
				totalTransfers -= event.amount;
			}

			const taxBasis = totalContributions + totalIncomeAllocations - totalDistributions + totalTransfers;
			const eventLabel =
				event.type === 'income_allocation'
					? 'Income Allocation'
					: event.type === 'distribution'
						? 'Distribution'
						: event.type === 'transfer_in'
							? 'Transfer In'
							: event.type === 'transfer_out'
								? 'Transfer Out'
								: 'Contribution';

			taxBasisByDate[event.date] = {
				value: taxBasis,
				contributions: totalContributions,
				incomeAllocations: totalIncomeAllocations,
				distributions: totalDistributions,
				label: eventLabel,
			};
		});

		// Build market value map by date using denomination-aware ownership fraction.
		// denomination-resolver priority: per-class equity.denomination → use_fund_language → 'shares'
		const denomination =
			shareClasses.length === 1 ? getEffectiveDenomination(clientData, shareClasses[0]) : clientData.use_fund_language ? 'dollars' : 'shares';

		let ownershipFraction = 0;
		if (denomination === 'shares') {
			// owned is a share count → fraction IS the count (marketValue = count × pricePerShare)
			ownershipFraction = totalOwned;
		} else if (denomination === 'dollars') {
			// owned is a capital account dollar amount → derive ownership % from totalShares config or event ratios
			let totalClassOwned = 0;
			shareClasses.forEach(function (sc) {
				const classConfig = clientData.equity?.[sc] || {};
				if (classConfig.totalShares !== undefined) {
					totalClassOwned += Number(classConfig.totalShares) || 0;
				} else if (clientData.metrics?.total_units !== undefined) {
					totalClassOwned += Number(clientData.metrics.total_units) || 0;
				}
			});

			if (totalClassOwned > 0) {
				ownershipFraction = totalOwned / totalClassOwned;
			} else {
				// Fallback: derive ownership % from event ratios (user amount / class total amount)
				let derivedFraction = 0;
				shareClasses.forEach(function (sc) {
					const userShares = userData.clients?.[client]?.shares?.[sc];
					if (!userShares?.events) return;
					const nowTs = Math.floor(Date.now() / 1000);
					const classEvents = (clientData.equity?.[sc]?.events || []).filter((e) => isPastOrDateless(e, nowTs));

					for (let i = userShares.events.length - 1; i >= 0; i--) {
						const ue = userShares.events[i];
						const ce = classEvents.find(function (e) {
							return e.nrid === ue.nrid;
						});
						if (!ce) continue;
						const userAmt = Number(ue.grossDollars) || 0;
						const classAmt = Number(ce.grossDollars) || 0;
						if (classAmt > 0 && userAmt > 0) {
							derivedFraction = userAmt / classAmt;
							break;
						}
					}
				});
				ownershipFraction = derivedFraction;
			}
		} else {
			// 'percent': owned is a percentage (e.g., 9.091%) → fraction = owned / 100
			ownershipFraction = totalOwned / 100;
		}
		const marketValueByDate = {};
		valuationPoints.forEach(function (point) {
			marketValueByDate[point.date] = ownershipFraction * point.sharePrice;
		});

		// Get all unique dates from both series, starting from the user's first event
		const firstEventDate = events.length > 0 ? events[0].date : 0;
		const allDates = new Set([...Object.keys(taxBasisByDate).map(Number), ...Object.keys(marketValueByDate).map(Number)]);
		const sortedDates = Array.from(allDates)
			.filter(function (d) {
				return d >= firstEventDate;
			})
			.sort((a, b) => a - b);

		// Build merged time series with forward-filled values
		// Format: [date, taxBasis, marketValue, tooltip]
		const timeSeries = [];
		let lastTaxBasis = 0;
		let lastTaxBasisData = { contributions: 0, incomeAllocations: 0, distributions: 0, label: 'Initial' };
		let lastMarketValue = null;

		// Pre-fill lastMarketValue from the most recent valuation before the first event
		// so the first data point has a market value even if it doesn't fall on a valuation date
		const valuationDates = Object.keys(marketValueByDate)
			.map(Number)
			.sort((a, b) => a - b);
		for (let i = valuationDates.length - 1; i >= 0; i--) {
			if (valuationDates[i] <= firstEventDate) {
				lastMarketValue = marketValueByDate[valuationDates[i]];
				break;
			}
		}

		sortedDates.forEach(function (date) {
			// Update tax basis if we have a value for this date
			if (taxBasisByDate[date]) {
				lastTaxBasis = taxBasisByDate[date].value;
				lastTaxBasisData = taxBasisByDate[date];
			}

			// Update market value if we have a value for this date
			if (marketValueByDate[date]) {
				lastMarketValue = marketValueByDate[date];
			}

			// Include breakdown data for rich tooltips: [date, taxBasis, marketValue, breakdownData]
			timeSeries.push([
				date,
				lastTaxBasis,
				lastMarketValue,
				{
					contributions: lastTaxBasisData.contributions,
					incomeAllocations: lastTaxBasisData.incomeAllocations,
					distributions: lastTaxBasisData.distributions,
					label: lastTaxBasisData.label,
				},
			]);
		});

		// Add current "today" point only if we have actual data
		const now = Date.now();
		const latestTaxBasis = lastTaxBasis;
		const latestMarketValue = lastMarketValue;

		if (events.length > 0 || valuationPoints.length > 0)
			timeSeries.push([
				now,
				latestTaxBasis,
				latestMarketValue,
				{
					contributions: lastTaxBasisData.contributions,
					incomeAllocations: lastTaxBasisData.incomeAllocations,
					distributions: lastTaxBasisData.distributions,
					label: 'Current',
				},
			]);

		const hasValuations = valuationPoints.length > 0;
		const hasEvents = events.length > 0;
		const unrealizedGain = latestMarketValue !== null ? latestMarketValue - latestTaxBasis : null;

		console.log('[TimeSeriesBuilder] Income allocations time series built:', timeSeries.length, 'points, hasEvents:', hasEvents);

		return {
			data: timeSeries,
			totalValue: latestTaxBasis,
			totalMarketValue: latestMarketValue,
			totalUnrealizedGain: unrealizedGain,
			hasEvents: hasEvents, // Flag to indicate shareholder has event data
			isDualLine: hasValuations, // Flag for graph rendering
			seriesLabels: ['Tax Basis', 'Market Value'],
		};
	},
};

export { TimeSeriesBuilder };
